import { type FC, useCallback, useEffect, useRef } from 'react';

interface Props {
  /** Co-occurring (longG, latG) pairs at peak combined load, per point (null = no telemetry). */
  ggLongG: (number | null)[];
  ggLatG: (number | null)[];
  scrubIndex: number | null;
  /** Index window the cloud is drawn for — follows the charts' shared zoom. Defaults to all. */
  cloudLo?: number;
  cloudHi?: number;
}

const SIZE = 200;
const MARGIN = 22; // room outside the outer ring for the axis + scale labels
const TRAIL = 12; // points before the cursor drawn as a fading trail
const MIN_FULL_SCALE = 0.4; // never zoom in tighter than ±0.4 g, so a gentle ride still reads
const FULL_SCALE_STEP = 0.1; // round the outer scale up to the next 1/10 g (snug, no extra headroom)

/**
 * The ride's friction-circle usage (longG vs latG) replayed by the scrub cursor — the
 * "second set" from the chart design, the same diagram as the live telemetry drawer but
 * driven by stored data. The whole ride is a faint cloud; the cursor's point (plus a short
 * trail) is highlighted. Fed real co-occurring pairs, so no fabricated points.
 *
 * The scale auto-fits the ride's own peak G (most riders never reach 1.5 g) with a little
 * headroom and nice ring steps, so the cloud fills the diagram instead of huddling near the
 * centre. The full-scale value is taken from the whole ride (not the zoom window) so it
 * stays stable while scrubbing.
 */
const RideGGChart: FC<Props> = ({ ggLongG, ggLatG, scrubIndex, cloudLo, cloudHi }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== SIZE * dpr || canvas.height !== SIZE * dpr) {
      canvas.width = SIZE * dpr;
      canvas.height = SIZE * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, SIZE, SIZE);

    // Full scale = the ride's largest per-axis G, padded up to a nice ring step.
    let dataMax = 0;
    for (let i = 0; i < ggLongG.length; i++) {
      const lo = ggLongG[i];
      const la = ggLatG[i];
      if (lo != null) dataMax = Math.max(dataMax, Math.abs(lo));
      if (la != null) dataMax = Math.max(dataMax, Math.abs(la));
    }
    const fitted = Math.max(MIN_FULL_SCALE, dataMax);
    const maxG = Math.ceil(fitted / FULL_SCALE_STEP - 1e-9) * FULL_SCALE_STEP;
    const ringStep = niceStep(maxG);

    const c = SIZE / 2;
    const radius = SIZE / 2 - MARGIN;
    const toX = (latG: number) => c + (latG / maxG) * radius;
    const toY = (longG: number) => c - (longG / maxG) * radius;

    // Grid: crosshair + rings at nice G steps, each labelled with its value.
    ctx.strokeStyle = 'rgba(255,255,255,0.09)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(c - radius, c);
    ctx.lineTo(c + radius, c);
    ctx.moveTo(c, c - radius);
    ctx.lineTo(c, c + radius);
    ctx.stroke();
    ctx.fillStyle = '#5b626c';
    ctx.font = '600 8px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    // Label on the upper-right diagonal (clear of the axis text + the on-axis cloud).
    const drawRing = (g: number, outer: boolean) => {
      const r = (g / maxG) * radius;
      ctx.beginPath();
      ctx.arc(c, c, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillText(`${fmtG(g)}${outer ? ' g' : ''}`, c + r * Math.SQRT1_2 + 2, c - r * Math.SQRT1_2);
    };
    // Interior rings at a nice step; the outer boundary always sits exactly at the scale max
    // (skip the last interior ring if it would crowd the boundary).
    for (let g = ringStep; g < maxG - ringStep * 0.5 + 1e-9; g += ringStep) drawRing(g, false);
    drawRing(maxG, true);

    ctx.fillStyle = '#646b76';
    ctx.font = '600 9px system-ui, sans-serif';
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'center';
    ctx.fillText('ACCEL', c, c - radius - 5);
    ctx.fillText('BRAKE', c, c + radius + 11);
    ctx.textAlign = 'right';
    ctx.fillText('L', c - radius - 4, c + 3);
    ctx.textAlign = 'left';
    ctx.fillText('R', c + radius + 4, c + 3);

    // Cloud over the visible window (the whole ride unless the charts are zoomed in).
    const lo0 = Math.max(0, cloudLo ?? 0);
    const hi0 = Math.min(ggLongG.length - 1, cloudHi ?? ggLongG.length - 1);
    ctx.fillStyle = 'rgba(255,176,32,0.18)';
    for (let i = lo0; i <= hi0; i++) {
      const lo = ggLongG[i];
      const la = ggLatG[i];
      if (lo == null || la == null) continue;
      ctx.beginPath();
      ctx.arc(toX(la), toY(lo), 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Trail up to the cursor
    if (scrubIndex != null) {
      for (let k = TRAIL; k >= 1; k--) {
        const i = scrubIndex - k;
        if (i < 0) continue;
        const lo = ggLongG[i];
        const la = ggLatG[i];
        if (lo == null || la == null) continue;
        ctx.fillStyle = `rgba(255,176,32,${(0.5 * (TRAIL - k + 1)) / TRAIL})`;
        ctx.beginPath();
        ctx.arc(toX(la), toY(lo), 3, 0, Math.PI * 2);
        ctx.fill();
      }
      const lo = ggLongG[scrubIndex];
      const la = ggLatG[scrubIndex];
      if (lo != null && la != null) {
        ctx.beginPath();
        ctx.arc(toX(la), toY(lo), 6.5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffb020';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.fill();
        ctx.stroke();
      }
    }
  }, [ggLongG, ggLatG, scrubIndex, cloudLo, cloudHi]);

  useEffect(() => {
    draw();
  }, [draw]);

  return <canvas ref={canvasRef} className="mg-gg-review" style={{ width: SIZE, height: SIZE }} />;
};

/** A "nice" ring step (1/2/5 × 10ⁿ) that puts ~3 rings inside `range`. */
function niceStep(range: number): number {
  const raw = range / 3;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / pow;
  const nice = n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10;
  return nice * pow;
}

/** Trim trailing zeros: 0.2 → "0.2", 1 → "1", 0.25 → "0.25". */
function fmtG(g: number): string {
  return (+g.toFixed(2)).toString();
}

export default RideGGChart;
